import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getMovieById, getRelatedMovies, getCategoryBySlug } from '@/mocks/movies';
import { siteConfig } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id || '');

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">影片不存在或已下架</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-2 inline-block">返回首页看看其他影片</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);
  const category = getCategoryBySlug(movie.category);
  const articleUrl = `${siteConfig.url}/article/${movie.id}`;
  const articleTitle = `${movie.title} - 小辣椒影院高清免费在线观看`;
  const articleDesc = `${movie.title}（${movie.englishTitle}），${movie.year}年${movie.region}${category ? category.name : '影视'}，导演${movie.director}，主演${movie.cast.join('、')}。${movie.description.slice(0, 100)}...`;
  const articleKeywords = `小辣椒影院,${movie.title},${movie.title}在线观看,${movie.title}免费,${movie.region}${category ? category.name : ''},${movie.tags.join(',')}`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={articleTitle}
        description={articleDesc}
        keywords={articleKeywords}
        canonicalUrl={articleUrl}
        ogType="article"
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.posterImage,
            dateCreated: String(movie.year),
            director: { '@type': 'Person', name: movie.director },
            actor: movie.cast.map((a: string) => ({ '@type': 'Person', name: a })),
            countryOfOrigin: { '@type': 'Country', name: movie.region },
            url: articleUrl,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '小辣椒影院', item: siteConfig.url },
              ...(category ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `${siteConfig.url}/category/${category.slug}` }] : []),
              { '@type': 'ListItem', position: category ? 3 : 2, name: movie.title, item: articleUrl },
            ],
          },
        ]}
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto pt-6 pb-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              {category && (
                <>
                  <Link to={`/category/${category.slug}`} className="hover:text-primary-500 transition-colors">{category.name}</Link>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </>
              )}
              <span className="text-foreground-800 font-medium truncate max-w-[200px]">{movie.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Header */}
        <section className="w-full px-4 md:px-6 pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="relative w-full sm:w-64 lg:w-72 aspect-[2/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={movie.posterImage}
                    alt={movie.title}
                    title={`${movie.title} - 小辣椒影院高清免费在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="flex items-center gap-1 bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    {movie.rating}
                  </span>
                  {movie.tags.map((tag) => (
                    <span key={tag} className="bg-secondary-100 text-secondary-700 px-2.5 py-1 rounded-full text-sm whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className="bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full text-sm whitespace-nowrap">
                    {movie.region}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 bg-background-100 rounded-xl p-5 mb-6">
                  <DetailItem label="导演" value={movie.director} />
                  <DetailItem label="年份" value={String(movie.year)} />
                  <DetailItem label="主演" value={movie.cast.join(' / ')} />
                  <DetailItem label="时长" value={movie.duration} />
                  <DetailItem label="地区" value={movie.region} />
                  <DetailItem label="更新" value={movie.updateTime} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-play-circle-fill w-5 h-5 flex items-center justify-center text-lg"></i>
                    立即播放
                  </a>
                  <a
                    href="#"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all duration-300 whitespace-nowrap border border-background-300"
                  >
                    <i className="ri-heart-line w-5 h-5 flex items-center justify-center"></i>
                    收藏
                  </a>
                  <a
                    href="#"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all duration-300 whitespace-nowrap border border-background-300"
                  >
                    <i className="ri-share-forward-line w-5 h-5 flex items-center justify-center"></i>
                    分享
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Synopsis */}
        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">剧情简介</h2>
            <div className="bg-background-100 rounded-xl p-6">
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {movie.description}
              </p>
            </div>
          </div>
        </section>

        {/* Cast */}
        <section className="w-full px-4 md:px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">主演阵容</h2>
            <div className="flex flex-wrap gap-3">
              {movie.cast.map((actor) => (
                <span
                  key={actor}
                  className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {actor}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Related */}
        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/article/${m.id}`}
                    className="group"
                    data-product-shop
                  >
                    <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={m.posterImage}
                        alt={m.title}
                        title={`${m.title} - 小辣椒影院相关推荐`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-md whitespace-nowrap">
                        {m.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {m.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{m.region} · {m.year}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-foreground-500 whitespace-nowrap">{label}：</span>
      <span className="text-sm text-foreground-800 truncate">{value}</span>
    </div>
  );
}